import { effect } from '@preact/signals'
import { ProjectService } from 'core'
import { createState } from 'shared'
import { navigate } from 'wouter-preact/use-browser-location'

import {
	addNewSmartContract,
	addSmartContractToProjectDetail,
	application,
	projectsState,
	resetSystemNotification,
	smartContractsState,
	updateProject
} from 'state'

// State for input errors
const inputErrorsState = createState<Record<string, string | undefined>>({})
export const inputErrors = inputErrorsState.state

// State for smart contract name input
const smartContractNameState = createState('')
export const smartContractName = smartContractNameState.state

// State for dApp name input
const dAppNameState = createState<{
	id: string | null
	name: string
} | null>(null)
export const dAppName = dAppNameState.state

// Function to update input errors
const onUpdateInputErrors = (field: string, error?: string) => {
	inputErrorsState.updateState(prevErrorState => {
		if (prevErrorState[field] === error) return prevErrorState

		return {
			...prevErrorState,
			[field]: error
		}
	})
}

// Validation for Smart Contract Name
export const validateSmartContractName = (
	smartContractValue: string,
	dAppNameValue: string
): string | undefined => {
	// Check if the combination of dAppName and smartContractName already exists
	const isExisting = smartContractsState.value.some(
		sc => sc.projectName === dAppNameValue && sc.name === smartContractValue
	)

	if (isExisting) {
		return 'The smart contract name is already in use'
	}

	const error = ProjectService.ValidateSmartContractName(smartContractValue)
	if (error && error.inputErrors?.smartContract) {
		return error.inputErrors.smartContract
	}

	return undefined
}

// Validation for dApp Name
export const validateDAppName = (
	dAppNameValue: string,
	smartContractNameValue: string
): string | undefined => {
	// Check if the combination of dAppName and smartContractName already exists
	const isExisting = smartContractsState.value.some(
		sc => sc.projectName === dAppNameValue && sc.name === smartContractNameValue
	)

	if (isExisting) {
		return 'The dApp name and smart contract name combination is already in use'
	}

	const error = ProjectService.ValidateDAppName(dAppNameValue)
	if (error && error.inputErrors?.project) {
		return error.inputErrors.project
	}

	return undefined
}

// Use effect to react to changes in smartContractName and dAppName
effect(() => {
	const smartContractNameValue = smartContractName.value
	const dAppNameValue = dAppName.value

	if (smartContractNameValue) {
		// Validate Smart Contract Name
		const smartContractError = validateSmartContractName(
			smartContractNameValue,
			dAppNameValue?.name || ''
		)
		onUpdateInputErrors('smart_contract_name', smartContractError)
	}

	if (dAppNameValue) {
		// Validate dApp Name
		const dAppError = validateDAppName(
			dAppNameValue.name,
			smartContractNameValue
		)
		onUpdateInputErrors('dapp_name', dAppError)
	}
})

export const setSmartContractName = (value: string) => {
	smartContractNameState.updateState(() => value)
}

export const setDAppName = (
	value: {
		id: string | null
		name: string
	} | null
) => {
	dAppNameState.updateState(() => value)
}

// Function to reset form and errors
export const resetFormAndErrors = () => {
	smartContractNameState.updateState(() => '')
	dAppNameState.updateState(() => null)
	inputErrorsState.updateState(() => ({}))
}

// TODO: Create test cases
export const createSmartContract = async () => {
	resetSystemNotification()

	let projectId = dAppName.value?.id

	if (!projectId) {
		const existedDApp = projectsState.value.find(
			project => project.name === dAppName.peek()?.name
		)

		projectId = existedDApp?.publicId
	}

	const { data, error } = await application.value
		.forManagingProjects()
		.createSmartContract({
			project: {
				name: dAppName.peek()!.name,
				projectId
			},
			smartContract: {
				name: smartContractName.peek()
			}
		})

	if (error) {
		return { error }
	}

	const dApp = data!.smartContracts.find(
		contract => contract.name === smartContractName.peek()
	)

	updateProject(data!)

	addNewSmartContract({
		...dApp!,
		projectName: data!.name,
		owner: data!.owner,
		projectId: data!.publicId,
		isCollaborator: data!.isCollaborator,
		color: data!.color!,
		isWorkspaceReady: false
	})

	addSmartContractToProjectDetail(dApp!)

	navigate(`/dapp/${data!.publicId}/smart-contract/${dApp!.name}/workspace`)

	return { error: null }
}
